/**
 * 装备面板 — 人形布局（15槽）
 */

import type { PlayerState } from '../engine/Player';
import type { EquipSlot } from '../data/equipment';
import { SLOT_NAMES, SLOT_ICONS, QUALITY_NAMES } from '../data/equipment';
import type { Equipment } from '../engine/EquipmentSystem';
import { getFinalStats, getEquippableForSlot } from '../engine/EquipmentSystem';

export type EquipAction = 'equip' | 'unequip' | 'enhance';
export interface EquipActionPayload { action: EquipAction; itemId?: string; slot?: EquipSlot; }

// 人形布局定义：[slot, css grid area]
const FIGURE_LAYOUT: { slot: EquipSlot; row: number; col: number; label: string }[] = [
  { slot: 'head', row: 1, col: 2, label: '头冠' },
  { slot: 'face', row: 2, col: 2, label: '面饰' },
  { slot: 'neck', row: 3, col: 2, label: '项链' },
  { slot: 'weapon', row: 4, col: 1, label: '武器' },
  { slot: 'body', row: 4, col: 2, label: '道袍' },
  { slot: 'offhand', row: 4, col: 3, label: '副手' },
  { slot: 'inner', row: 5, col: 2, label: '内甲' },
  { slot: 'arms', row: 6, col: 1, label: '护臂' },
  { slot: 'back', row: 6, col: 3, label: '披风' },
  { slot: 'hands', row: 7, col: 1, label: '手套' },
  { slot: 'ring1', row: 7, col: 2, label: '戒1' },
  { slot: 'ring2', row: 7, col: 3, label: '戒2' },
  { slot: 'waist', row: 8, col: 2, label: '腰带' },
  { slot: 'legs', row: 9, col: 2, label: '护腿' },
  { slot: 'feet', row: 10, col: 2, label: '战靴' },
];

export class EquipmentPanel {
  private container: HTMLElement;
  private onAction: ((payload: EquipActionPayload) => void) | null = null;
  private selectedSlot: EquipSlot | null = null;

  constructor(containerId: string) {
    const el = document.getElementById(containerId);
    if (!el) throw new Error(`Element #${containerId} not found`);
    this.container = el;
    this.container.className = 'panel';
  }

  setOnAction(callback: (payload: EquipActionPayload) => void): void { this.onAction = callback; }

  render(player: PlayerState): void {
    // 保存当前选中槽位
    const prevSlot = this.selectedSlot;

    const cells = FIGURE_LAYOUT.map(({ slot, row, col, label }) => {
      const eq = player.equipment[slot] as Equipment | undefined;
      const icon = SLOT_ICONS[slot];
      let inner: string;
      if (eq) {
        const levelStr = eq.enhanceLevel > 0 ? ` +${eq.enhanceLevel}` : '';
        const perfectStr = eq.isPerfect ? '🔥' : '';
        inner = `<span style="font-weight:bold;font-size:0.75em;cursor:pointer;" class="equip-slot-item" data-slot="${slot}">${eq.name}${levelStr}${perfectStr}</span>`;
      } else {
        inner = `<span style="font-size:0.7em;color:var(--text-secondary);cursor:pointer;" class="equip-slot-item equip-empty" data-slot="${slot}">${icon} ${label}</span>`;
      }
      return `<div style="grid-row:${row};grid-column:${col};text-align:center;padding:2px;min-height:32px;display:flex;align-items:center;justify-content:center;">${inner}</div>`;
    }).join('');

    this.container.innerHTML = `
      <div class="panel-title">🛡️ 装备</div>
      <div class="equip-figure" style="display:grid;grid-template-columns:1fr 1fr 1fr;grid-template-rows:repeat(10,auto);gap:2px;">
        ${cells}
      </div>
      <div id="equip-detail" style="margin-top:6px;min-height:40px;"></div>
    `;

    // 绑定槽位点击
    this.container.querySelectorAll('.equip-slot-item').forEach((el) => {
      el.addEventListener('click', () => {
        const slot = (el as HTMLElement).dataset.slot as EquipSlot;
        this.showDetail(player, slot);
      });
    });

    // 恢复之前选中的详情
    if (prevSlot) this.showDetail(player, prevSlot);
  }

  private showDetail(player: PlayerState, slot: EquipSlot): void {
    this.selectedSlot = slot;
    const eq = player.equipment[slot] as Equipment | undefined;
    const detail = this.container.querySelector('#equip-detail') as HTMLElement;
    if (!detail) return;

    if (eq) {
      const stats = getFinalStats(eq);
      const qualityName = (QUALITY_NAMES as any)[eq.quality] ?? eq.quality;
      const levelStr = eq.enhanceLevel > 0 ? ` +${eq.enhanceLevel}` : '';
      const perfectStr = eq.isPerfect ? ' 🔥极品' : '';
      let statsHtml = '';
      if (stats.atk) statsHtml += `<div>⚔️ 攻击+${stats.atk}</div>`;
      if (stats.def) statsHtml += `<div>🛡️ 防御+${stats.def}</div>`;
      if (stats.hp) statsHtml += `<div>❤️ HP+${stats.hp}</div>`;
      if (stats.mp) statsHtml += `<div>💙 MP+${stats.mp}</div>`;
      if (stats.cap) statsHtml += `<div>🎒 容量${stats.cap}</div>`;

      detail.innerHTML = `
        <div style="border:1px solid rgba(200,160,60,0.3);border-radius:4px;padding:6px;">
          <div style="font-weight:bold;">${eq.name}${levelStr} <span style="color:var(--text-gold);">[${qualityName}]</span>${perfectStr}</div>
          <div style="font-size:0.8em;">品质roll: ${eq.rollPct}%</div>
          ${statsHtml}
          <div style="display:flex;gap:4px;margin-top:4px;">
            <button class="btn btn-small" data-act="unequip" data-slot="${slot}">卸下</button>
            <button class="btn btn-small" data-act="enhance" data-slot="${slot}" ${eq.enhanceLevel >= 13 ? 'disabled' : ''}>${eq.enhanceLevel >= 13 ? '已满级' : '🔨 强化'}</button>
          </div>
        </div>
      `;
    } else {
      // 空槽位：显示可装备列表
      const templates = getEquippableForSlot(slot);
      const owned = player.inventory.filter((i: any) => i.slot === slot || (slot === 'ring2' && i.slot === 'ring1'));
      let listHtml = '';
      if (owned.length > 0) {
        listHtml += '<div style="font-size:0.8em;color:var(--text-gold);margin-bottom:4px;">背包中的装备：</div>';
        for (const item of owned) {
          listHtml += `<div style="display:flex;justify-content:space-between;align-items:center;padding:2px 0;font-size:0.8em;">
            <span>${item.name} [${(QUALITY_NAMES as any)[item.quality] ?? item.quality}]${item.isPerfect ? '🔥' : ''}</span>
            <button class="btn btn-small" data-act="equip" data-item="${item.id}">装备</button>
          </div>`;
        }
      } else {
        listHtml += '<div style="font-size:0.75em;color:var(--text-secondary);">背包中没有此槽位的装备</div>';
      }
      detail.innerHTML = `
        <div style="border:1px solid rgba(255,255,255,0.1);border-radius:4px;padding:6px;">
          <div style="font-size:0.85em;">${SLOT_ICONS[slot]} ${SLOT_NAMES[slot]} — 空</div>
          ${listHtml}
        </div>
      `;
    }

    // 绑定按钮
    detail.querySelectorAll('button[data-act]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const act = (btn as HTMLElement).dataset.act as EquipAction;
        const s = (btn as HTMLElement).dataset.slot as EquipSlot;
        const itemId = (btn as HTMLElement).dataset.item;
        if (this.onAction) this.onAction({ action: act, slot: s, itemId });
      });
    });
  }
}
